import { useEffect, useState } from 'react';

import useSWR from 'swr';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Lib
import { dayOfWeek } from '@/lib/scheduler/calendar/calendar';
import { FACEBOOK, INSTAGRAM, LINKEDIN, TIKTOK_BUSINESS, TWITTER } from '@/lib/socialProfile';

// Routes
import { ANALYTICS, GROUP_ANALYTICS } from '@/routes/apiEndpoints';

const fields = {
    [INSTAGRAM]: ['followers_online', 'best_time_to_post'],
    [FACEBOOK]: ['fans_online', 'best_time_to_post'],
    [TWITTER]: ['best_time_to_post'],
    [LINKEDIN]: ['best_time_to_post'],
    [TIKTOK_BUSINESS]: ['best_time_to_post']
};

const formatEvent = (data) => {
    const filterHighligh = data.filter((item) => item.highlight);

    return filterHighligh.map((item) => {
        return {
            hour: item.date,
            day_of_week: dayOfWeek[item.day],
            value: item?.value ?? 0
        };
    });
};

const useFoBttpEvents = ({ activeSPID, activeGroupID }) => {
    const [dataEvents, setDataEvents] = useState({
        followers_online: [],
        best_time_to_post: []
    });
    const { activeSocialProfile } = useActiveSocialProfile();
    const requestParams = {
        period: 'last_3_months',
        fields: fields[activeSocialProfile?.social_network_type ?? INSTAGRAM]
    };

    const endpoint = activeSPID ? ANALYTICS : GROUP_ANALYTICS;
    const endpointParams = activeSPID ? { SPID: activeSPID } : { GROUP_ID: activeGroupID };

    const {
        data,
        mutate: mutateAnalytics,
        error: errorFoBttpEvents,
        isLoading: isLoadingFoBttpEvents
    } = useSWR(
        [injectEndpointUrlParams(endpoint, endpointParams), ...Object.values(requestParams)],
        ([url]) =>
            get(url, requestParams).then((res) => {
                const followersOnlineIdChart =
                    activeSocialProfile?.social_network_type === FACEBOOK
                        ? 'fans_online'
                        : 'followers_online';

                let analyticsData = res.data.analytics;
                if (Array.isArray(res?.data?.analytics)) {
                    analyticsData = res?.data?.analytics?.find(
                        (item) => item?.group === activeGroupID
                    );
                }

                return {
                    followers_online: analyticsData?.[followersOnlineIdChart] ?? null,
                    best_time_to_post: analyticsData?.best_time_to_post ?? null
                };
            }),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false
        }
    );

    useEffect(() => {
        if (data && !errorFoBttpEvents) {
            const { followers_online, best_time_to_post } = data;
            let followerOnlineFormatted = [];
            let bestTimeToPostFormatted = [];

            if (followers_online) {
                followerOnlineFormatted = followers_online?.graphData
                    ? formatEvent(followers_online.graphData)
                    : [];
            }

            if (best_time_to_post) {
                bestTimeToPostFormatted = best_time_to_post?.graphData
                    ? formatEvent(best_time_to_post.graphData)
                    : [];
            }

            setDataEvents({
                followers_online: followerOnlineFormatted,
                best_time_to_post: bestTimeToPostFormatted
            });
        }
    }, [data, errorFoBttpEvents]);

    return {
        followersOnlineData: dataEvents.followers_online,
        bestTimeToPostData: dataEvents.best_time_to_post,
        mutateAnalytics,
        errorFoBttpEvents,
        isLoadingFoBttpEvents
    };
};

export default useFoBttpEvents;
